import { SETUP_CHECKLIST_ITEMS, type SetupChecklistItem } from "@unbox/api";
import {
  AlertTriangle,
  Announcement03,
  CurrencyDollarCircle,
  Feather,
  File02,
  Package,
  Send01,
  Tag01,
} from "@untitledui/icons";
import type { FC, SVGProps } from "react";

export type SetupGroupId = "brand" | "spend" | "creators" | "time";

export type SetupGroup = { id: SetupGroupId; eyebrow: string };

export const SETUP_GROUPS: SetupGroup[] = [
  { id: "brand", eyebrow: "Protect your brand" },
  { id: "spend", eyebrow: "Control your spend" },
  { id: "creators", eyebrow: "Stay in control of your creators" },
  { id: "time", eyebrow: "Save yourself time" },
];

export type SetupDestinationFlags = { newCollabsGroups: boolean };

type SetupItemPresentation = {
  group: SetupGroupId;
  label: string;
  cta: string;
  icon: FC<SVGProps<SVGSVGElement>>;
  destination: (base: string, flags: SetupDestinationFlags) => string;
};

export type SetupItemDefinition = SetupItemPresentation & { item: SetupChecklistItem };

const SETUP_ITEM_PRESENTATION: Record<SetupChecklistItem, SetupItemPresentation> = {
  risks: {
    group: "brand",
    label: "Be confident you're monitoring videos for the right risks",
    cta: "Review Risks",
    icon: AlertTriangle,
    destination: (base) => `${base}/safety/configure`,
  },
  policies: {
    group: "brand",
    label: "Decide how risks are handled and action them automatically with policies",
    cta: "Set Policies",
    icon: Feather,
    destination: (base) => `${base}/safety/configure`,
  },
  tiktokAds: {
    group: "spend",
    label: "Control which videos get your ad spend on TikTok",
    cta: "Connect TikTok Ads",
    icon: CurrencyDollarCircle,
    destination: (base) => `${base}/settings/integrations`,
  },
  allocations: {
    group: "spend",
    label: "Approve samples with confidence by setting allocations",
    cta: "Set Allocations",
    icon: Package,
    destination: (base) => `${base}/collabs/samples`,
  },
  cohorts: {
    group: "creators",
    label: "Organise and target the right creators by defining cohorts",
    cta: "Define Cohorts",
    icon: Tag01,
    destination: (base) => `${base}/creators/cohorts`,
  },
  collabs: {
    group: "creators",
    label: "See collaborations your way, not by TikTok's limitations",
    cta: "Create Collabs",
    icon: Announcement03,
    destination: (base, flags) => `${base}/${flags.newCollabsGroups ? "collab-groups" : "collaborations"}`,
  },
  outreach: {
    group: "time",
    label: "Reach creators while you sleep by automating outreach",
    cta: "Turn On Outreach",
    icon: Send01,
    destination: (base) => `${base}/automate-outreach`,
  },
  templates: {
    group: "time",
    label: "Keep every creator conversation consistent with message templates",
    cta: "Create Templates",
    icon: File02,
    destination: (base) => `${base}/creators/message-templates`,
  },
};

export const SETUP_ITEMS: SetupItemDefinition[] = SETUP_CHECKLIST_ITEMS.map((item) => ({
  item,
  ...SETUP_ITEM_PRESENTATION[item],
}));

export const setupItemsByGroup = (group: SetupGroupId): SetupItemDefinition[] =>
  SETUP_ITEMS.filter((definition) => definition.group === group);

export const setupSearchFor = (item: SetupChecklistItem): { tab: "risks" | "policies" } | undefined => {
  if (item === "risks") return { tab: "risks" };
  if (item === "policies") return { tab: "policies" };
  return undefined;
};
